'use client';
import React, { useRef, useEffect, useState } from 'react';

interface Service {
  icon: string;
  title: string;
  description: string;
  href: string;
  waText: string;
  featured?: boolean;
}

const services: Service[] = [
  {
    icon: '⚡',
    title: 'Quantum Karmic Healing',
    description: 'Release deep karmic patterns, soul imprints, and energetic blocks that hold you back from living your fullest life.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Quantum%20Karmic%20Healing',
    waText: 'Quantum Karmic Healing',
    featured: true,
  },
  {
    icon: '🧠',
    title: 'Clinical Hypnotherapy',
    description: 'Access the subconscious mind to reprogram limiting beliefs, overcome fears, and create lasting positive change.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Clinical%20Hypnotherapy',
    waText: 'Clinical Hypnotherapy',
  },
  {
    icon: '🔮',
    title: 'Past Life Regression',
    description: 'Journey through past life memories to heal karmic connections, emotional blocks, and soul contracts.',
    href: '/services/past-life-regression',
    waText: 'Past Life Regression',
  },
  {
    icon: '👨‍👩‍👧',
    title: 'Family Constellation',
    description: 'Heal generational trauma and family system entanglements to restore harmony and break inherited patterns.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Family%20Constellation',
    waText: 'Family Constellation',
  },
  {
    icon: '📖',
    title: 'Akashic Record Reading & Healing',
    description: 'Access your soul\'s cosmic library to discover soul purpose, life lessons, karmic patterns, and relationship insights.',
    href: '/services/akashic-records',
    waText: 'Akashic Record Reading',
    featured: true,
  },
  {
    icon: '🌸',
    title: 'Reiki Healing',
    description: 'Channel universal life force energy to restore balance, promote deep relaxation, and accelerate natural healing.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Reiki%20Healing',
    waText: 'Reiki Healing',
  },
  {
    icon: '🃏',
    title: 'Tarot Guidance',
    description: 'Gain clarity and insight into life situations, relationships, career, and spiritual path through intuitive tarot readings.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Tarot%20Guidance',
    waText: 'Tarot Guidance',
  },
  {
    icon: '💚',
    title: 'Emotional Wellness Sessions',
    description: 'Heal emotional wounds, release anxiety, stress, and grief through personalized energy healing and coaching.',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20Emotional%20Wellness%20Sessions',
    waText: 'Emotional Wellness Sessions',
  },
];

function ServiceCard({ service, index }: { service: Service; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), index * 80);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className={`glow-card p-6 relative overflow-hidden transition-all duration-700 h-full flex flex-col ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${service.featured ? 'border-[#c9a84c]/40' : ''}`}
    >
      {service.featured && (
        <div className="absolute top-3 right-3">
          <span className="text-[10px] tracking-widest uppercase text-[#c9a84c] border border-[#c9a84c]/40 px-2 py-0.5 rounded-full bg-[#c9a84c]/10">
            Popular
          </span>
        </div>
      )}

      <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-[#c9a84c]/30 rounded-tl-xl" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-[#c9a84c]/30 rounded-br-xl" />

      <div className="text-3xl mb-4">{service.icon}</div>
      <h3 className="font-serif text-xl font-semibold mb-3 text-[#e8dff5]">{service.title}</h3>
      <p className="text-[#c4b5fd]/65 text-sm leading-relaxed mb-5 flex-1">{service.description}</p>

      <div className="flex items-center gap-3 flex-wrap">
        <a
          href={service.href}
          target={service.href.startsWith('http') ? '_blank' : undefined}
          rel={service.href.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="text-xs tracking-widest uppercase font-semibold px-4 py-2 rounded-full transition-all duration-300 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 100%)',
            color: '#0d0818',
          }}
        >
          Learn More
        </a>
        <a
          href={`https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20${encodeURIComponent(service.waText)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs tracking-widest uppercase font-semibold px-4 py-2 rounded-full transition-all duration-300 hover:scale-105"
          style={{
            background: 'rgba(37,211,102,0.12)',
            border: '1px solid rgba(37,211,102,0.4)',
            color: '#25D366',
          }}
        >
          Book Now
        </a>
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/4 glow-orb-lavender" style={{ width: '450px', height: '450px', opacity: 0.45 }} />
        <div className="absolute bottom-0 right-1/4 glow-orb-gold" style={{ width: '350px', height: '350px', opacity: 0.3 }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">1:1 Healing Sessions</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
          </div>
          <h2
            className="font-serif text-4xl md:text-5xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Healing Services
          </h2>
          <div className="section-divider mb-4" />
          <p className="text-[#c4b5fd]/65 max-w-2xl mx-auto text-base leading-relaxed">
            Personalized 1:1 healing sessions designed to address your unique needs — from emotional release to deep soul transformation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-[#c4b5fd]/50 text-sm mb-4">Not sure which service is right for you?</p>
          <a
            href="https://wa.me/917049753125?text=Hello%2C%20I%20need%20guidance%20on%20which%20healing%20service%20is%20right%20for%20me"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-gold inline-block"
          >
            Get Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
